// Cutdown helper (finishing bundle 2D): shorter 60, 30 or 15 second versions
// of a script, made by deleting whole spoken Audio sentences.
//
// Deletion only, enforced by shape. The server numbers every spoken sentence
// and the model returns only sentence numbers, ranked by importance; the
// server keeps them from the top until the word target is met, then builds
// the new doc from the SOURCE doc. Nothing the model writes is ever copied
// into a script, so a cut can't rewrite, reorder or add a word, and Visual cells,
// on-screen text cells and every non-row node pass through untouched.
//
// Pure and framework-free: the worker builds docs with it and the admin UI
// shares its types and verdict rules.

import { isSpokenAudioType, normalizeAudioType } from './audioTypes';
import { countWords, spokenText, wordsToSeconds } from './runtime';

export const CUTDOWN_LENGTHS = [60, 30, 15] as const;
export type CutdownLength = (typeof CUTDOWN_LENGTHS)[number];

export function isCutdownLength(v: unknown): v is CutdownLength {
  return (CUTDOWN_LENGTHS as readonly unknown[]).includes(v);
}

/** "On target" means within 15% of the target either way. */
export const ON_TARGET_TOLERANCE = 0.15;
export type CutdownVerdict = 'on_target' | 'over' | 'under';

/** Verdict on an unrounded runtime: 25.5 to 34.5s is on target for 30s. */
export function cutdownVerdict(seconds: number, target: CutdownLength): CutdownVerdict {
  // Round away float noise so the boundaries (e.g. 34.5) stay inclusive.
  const slack = Math.round(target * ON_TARGET_TOLERANCE * 1000) / 1000;
  const diff = Math.round((seconds - target) * 1000) / 1000;
  if (Math.abs(diff) <= slack) return 'on_target';
  return diff > 0 ? 'over' : 'under';
}

/** Word budget for a length at the runtime badge's pace (60s is 140 words). */
export function targetWords(target: CutdownLength): number {
  return Math.round(target / wordsToSeconds(1));
}

const LENGTH_TAG_RE = /\s*\(\d+s\)\s*$/;

/**
 * "{title} (30s)". A trailing length tag such as "(60s)" is replaced, not
 * stacked. A title already used in the project (case-insensitive) gets " 2",
 * then " 3", and so on.
 */
export function cutdownTitle(
  sourceTitle: string,
  target: CutdownLength,
  taken: Iterable<string>,
): string {
  const base = sourceTitle.trim().replace(LENGTH_TAG_RE, '');
  const title = `${base} (${target}s)`;
  const used = new Set<string>();
  for (const t of taken) used.add(t.trim().toLowerCase());
  if (!used.has(title.toLowerCase())) return title;
  for (let n = 2; ; n++) {
    const next = `${title} ${n}`;
    if (!used.has(next.toLowerCase())) return next;
  }
}

// ---------- Doc walking ----------

interface PMNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown> | null;
  content?: PMNode[];
  marks?: unknown[];
}

export interface CutdownSentence {
  /** 1-based, numbered across the whole doc in reading order. */
  id: number;
  text: string;
  words: number;
  /** 1-based scriptRow number. */
  row: number;
}

/** A slice of a paragraph's plain text; `id` is null for whitespace-only. */
interface Segment {
  start: number;
  end: number;
  id: number | null;
}

interface ParagraphPlan {
  node: PMNode;
  text: string;
  segments: Segment[];
}

interface CellPlan {
  cell: PMNode;
  /** Spoken audio cell: its sentences are numbered and can be cut. */
  cuttable: boolean;
  paragraphs: ParagraphPlan[];
}

interface RowPlan {
  number: number;
  section: string | null;
  audio: CellPlan | null;
  visuals: PMNode | null;
}

interface DocPlan {
  rows: RowPlan[];
  sentences: CutdownSentence[];
}

function isAudioKind(cell: PMNode): boolean {
  // Schema default for a cell with no explicit kind is 'audio' (runtime.ts).
  return ((cell.attrs?.kind as string | undefined) ?? 'audio') === 'audio';
}

function nodeText(node: PMNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  return (node.content ?? []).map(nodeText).join('');
}

// Titles and short forms that end in a full stop without ending a sentence.
const ALWAYS_ABBREV = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'inc', 'ltd', 'co', 'pty',
  'jr', 'sr', 'no', 'st', 'mt', 'vs', 'etc', 'approx',
]);
const TERMINATORS = '.!?…。！？';
const CLOSERS = '"\'”’)]」』）］';

/** Sentence ranges within a paragraph's plain text. Trailing whitespace
 *  belongs to the sentence before it, so removing a middle sentence leaves
 *  "A. C." with single spacing. */
export function sentenceRanges(text: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  let start = 0;
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    let end = -1;
    if (ch === '\n') {
      end = i + 1;
    } else if (TERMINATORS.includes(ch)) {
      let j = i + 1;
      while (j < text.length && TERMINATORS.includes(text[j])) j++;
      while (j < text.length && CLOSERS.includes(text[j])) j++;
      if (j === text.length || /\s/.test(text[j]) || '。！？'.includes(ch)) {
        const next = text.slice(j).match(/[\p{L}\p{N}]/u)?.[0];
        const lowercaseNext = !!next && /\p{Ll}/u.test(next);
        if (!lowercaseNext && !(ch === '.' && isAbbreviation(text, start, i))) end = j;
      }
      if (end === -1) {
        i = j;
        continue;
      }
    }
    if (end === -1) {
      i++;
      continue;
    }
    // Swallow the whitespace that follows, stopping after a line break so a
    // hardBreak always ends exactly one sentence.
    while (end < text.length && /\s/.test(text[end]) && text[end - 1] !== '\n') end++;
    ranges.push([start, end]);
    start = end;
    i = end;
  }
  if (start < text.length) ranges.push([start, text.length]);
  return ranges;
}

function isAbbreviation(text: string, start: number, dot: number): boolean {
  const token = text.slice(start, dot).match(/([A-Za-z.]+)$/)?.[1];
  if (!token) return false;
  return ALWAYS_ABBREV.has(token.toLowerCase()) || /^[A-Z]$/.test(token) ||
    /^(?:[A-Za-z]\.)+[A-Za-z]$/.test(token);
}

function planDoc(doc: unknown): DocPlan {
  const rows: RowPlan[] = [];
  const sentences: CutdownSentence[] = [];
  let section: string | null = null;

  const planCell = (cell: PMNode, rowNumber: number): CellPlan => {
    const cuttable = isAudioKind(cell) && isSpokenAudioType(cell.attrs?.audioType);
    const paragraphs = (cell.content ?? []).map((p): ParagraphPlan => {
      const text = spokenText({ content: p.content });
      if (!cuttable) return { node: p, text, segments: [{ start: 0, end: text.length, id: null }] };
      const segments = sentenceRanges(text).map(([start, end]): Segment => {
        const slice = text.slice(start, end);
        if (!slice.trim()) return { start, end, id: null };
        const id = sentences.length + 1;
        sentences.push({
          id,
          text: slice.replace(/\s+/g, ' ').trim(),
          words: countWords(slice),
          row: rowNumber,
        });
        return { start, end, id };
      });
      return { node: p, text, segments };
    });
    return { cell, cuttable, paragraphs };
  };

  const walk = (nodes: PMNode[] | undefined) => {
    for (const node of nodes ?? []) {
      if (!node || typeof node !== 'object') continue;
      if (node.type === 'heading') {
        const level = Number(node.attrs?.level ?? 1);
        if (level >= 2) section = nodeText(node).trim() || null;
        continue;
      }
      if (node.type === 'scriptRow') {
        const number = rows.length + 1;
        const cells = node.content ?? [];
        const audioCell = cells.find((c) => isAudioKind(c)) ?? null;
        const visuals = cells.find((c) => c.attrs?.kind === 'visuals') ?? null;
        rows.push({
          number,
          section,
          audio: audioCell ? planCell(audioCell, number) : null,
          visuals,
        });
        continue;
      }
      walk(node.content);
    }
  };
  walk((doc as PMNode | null)?.content);
  return { rows, sentences };
}

/** Numbered sentences of every spoken Audio cell, in reading order. On-screen
 *  text, Visual cells, headings and other prose are never numbered. */
export function splitSentences(doc: unknown): CutdownSentence[] {
  return planDoc(doc).sentences;
}

// ---------- Building the cut ----------

function sameMarks(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
}

/** Rebuild a paragraph's inline content keeping only characters inside
 *  `keep` ranges, preserving marks, then trim the paragraph's ends. */
function sliceInline(nodes: PMNode[], keepRanges: Array<[number, number]>): PMNode[] {
  const kept = (pos: number) => keepRanges.some(([a, b]) => pos >= a && pos < b);
  const out: PMNode[] = [];
  const push = (node: PMNode) => {
    const last = out[out.length - 1];
    if (node.type === 'text' && last?.type === 'text' && sameMarks(last.marks, node.marks)) {
      last.text = (last.text ?? '') + (node.text ?? '');
    } else {
      out.push(node);
    }
  };
  let pos = 0;
  for (const node of nodes) {
    if (node.type === 'text') {
      const text = node.text ?? '';
      let piece = '';
      for (let k = 0; k < text.length; k++) {
        if (kept(pos + k)) piece += text[k];
      }
      if (piece) push({ ...structuredClone(node), text: piece });
      pos += text.length;
    } else if (node.type === 'hardBreak') {
      if (kept(pos)) push(structuredClone(node));
      pos += 1;
    } else {
      push(structuredClone(node));
    }
  }
  // Trim leading and trailing whitespace and stray line breaks.
  while (out.length) {
    const first = out[0];
    if (first.type === 'hardBreak') { out.shift(); continue; }
    if (first.type === 'text') {
      first.text = (first.text ?? '').replace(/^\s+/, '');
      if (!first.text) { out.shift(); continue; }
    }
    break;
  }
  while (out.length) {
    const last = out[out.length - 1];
    if (last.type === 'hardBreak') { out.pop(); continue; }
    if (last.type === 'text') {
      last.text = (last.text ?? '').replace(/\s+$/, '');
      if (!last.text) { out.pop(); continue; }
    }
    break;
  }
  return out;
}

function cutCell(plan: CellPlan, keep: Set<number>): PMNode[] {
  let hadSentences = false;
  let keptAny = false;
  const content: PMNode[] = [];
  for (const p of plan.paragraphs) {
    const numbered = p.segments.filter((s) => s.id !== null);
    if (numbered.length === 0) {
      content.push(structuredClone(p.node));
      continue;
    }
    hadSentences = true;
    const kept = numbered.filter((s) => keep.has(s.id as number));
    if (kept.length === 0) continue; // paragraph fully cut: drop it
    keptAny = true;
    if (kept.length === numbered.length) {
      content.push(structuredClone(p.node));
      continue;
    }
    const keepRanges = p.segments
      .filter((s, i, segments) => s.id !== null ? keep.has(s.id) :
        // Blank-line segments belong to the preceding sentence's spacing.
        // Delete them with that sentence, preserving the earlier gap.
        keep.has(segments.slice(0, i).reverse().find((seg) => seg.id !== null)?.id ?? -1))
      .map((s): [number, number] => [s.start, s.end]);
    const inline = sliceInline(p.node.content ?? [], keepRanges);
    const next: PMNode = { ...structuredClone(p.node) };
    if (inline.length) next.content = inline;
    else delete next.content;
    content.push(next);
  }
  if (hadSentences && !keptAny) return [{ type: 'paragraph' }];
  return content.length ? content : [{ type: 'paragraph' }];
}

function toKeepSet(keep: Iterable<number>, max: number): Set<number> {
  const set = new Set<number>();
  for (const id of keep) {
    if (Number.isInteger(id) && id >= 1 && id <= max) set.add(id);
  }
  return set;
}

/**
 * The source doc with every unkept sentence deleted from the spoken Audio
 * cells. Ids outside 1..n are ignored. When `newTitle` is given the first
 * top-level H1 takes it, because the editor derives the script title from
 * that heading on save. Rows are never removed: a row whose Audio is fully
 * cut keeps its Visual and gets one empty Audio paragraph.
 */
export function buildCutDoc(doc: unknown, keep: Iterable<number>, newTitle?: string): unknown {
  const plan = planDoc(doc);
  const keepSet = toKeepSet(keep, plan.sentences.length);
  const replacements = new Map<PMNode, PMNode[]>();
  for (const row of plan.rows) {
    if (row.audio?.cuttable) replacements.set(row.audio.cell, cutCell(row.audio, keepSet));
  }

  const rebuild = (node: PMNode): PMNode => {
    const replaced = replacements.get(node);
    if (replaced) {
      const cell: PMNode = { ...node, content: replaced };
      if (node.attrs) cell.attrs = structuredClone(node.attrs);
      return cell;
    }
    const copy: PMNode = { ...node };
    if (node.attrs) copy.attrs = structuredClone(node.attrs);
    if (node.marks) copy.marks = structuredClone(node.marks);
    if (Array.isArray(node.content)) copy.content = node.content.map(rebuild);
    return copy;
  };
  const out = rebuild(doc as PMNode);
  if (newTitle !== undefined && Array.isArray(out.content)) {
    const h1 = out.content.find((n) => n.type === 'heading' && Number(n.attrs?.level) === 1);
    if (h1) {
      if (newTitle) h1.content = [{ type: 'text', text: newTitle }];
      else delete h1.content;
    }
  }
  return out;
}

// ---------- Review preview ----------

export interface CutdownSegment {
  text: string;
  /** Struck through in the review: comes out of the Audio. */
  removed: boolean;
}

export interface CutdownPreviewRow {
  /** 1-based scriptRow number. */
  number: number;
  /** Nearest heading (level 2 or deeper) above the row. */
  section: string | null;
  audio_type: string | null;
  /** On-screen text: never struck and never counted. */
  silent: boolean;
  /** One array per Audio paragraph; segments in reading order. */
  audio: CutdownSegment[][];
  visuals: string[];
  /** The Audio had at least one sentence and the cut keeps none. */
  no_audio: boolean;
}

/** Read-only view of the source rows with the cut marked as struck text. */
export function buildCutPreview(doc: unknown, keep: Iterable<number>): CutdownPreviewRow[] {
  const plan = planDoc(doc);
  const keepSet = toKeepSet(keep, plan.sentences.length);
  return plan.rows.map((row) => {
    const audio: CutdownSegment[][] = [];
    let hadSentences = false;
    let keptAny = false;
    for (const p of row.audio?.paragraphs ?? []) {
      const segs: CutdownSegment[] = [];
      for (const s of p.segments) {
        const text = p.text.slice(s.start, s.end);
        if (!text) continue;
        const removed = s.id !== null && !keepSet.has(s.id);
        if (s.id !== null) {
          hadSentences = true;
          if (!removed) keptAny = true;
        }
        const last = segs[segs.length - 1];
        if (last && last.removed === removed) last.text += text;
        else segs.push({ text, removed });
      }
      audio.push(segs);
    }
    const audioType = row.audio ? normalizeAudioType(row.audio.cell.attrs?.audioType) : null;
    return {
      number: row.number,
      section: row.section,
      audio_type: audioType,
      silent: !!row.audio && !row.audio.cuttable,
      audio,
      visuals: (row.visuals?.content ?? []).map((p) => nodeText(p).trim()).filter(Boolean),
      no_audio: hadSentences && !keptAny,
    };
  });
}

// ---------- Model reply ----------

/**
 * Read the model's ranking: sentence numbers, most important first. Accepts
 * { ranked: [...] } (or "ranking" / "order" / "keep" / "ids", or a bare
 * array) of integers or strings like "3" / "S3". Returns unique ids within
 * 1..max in the model's order, or null when the reply is unusable or names
 * no real sentence. Anything else in the reply is ignored.
 */
export function parseRanking(raw: unknown, max: number): number[] | null {
  let list: unknown = raw;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    list = obj.ranked ?? obj.ranking ?? obj.order ?? obj.keep ?? obj.ids;
  }
  if (!Array.isArray(list)) return null;
  const ids: number[] = [];
  for (const entry of list) {
    let n: number | null = null;
    if (typeof entry === 'number' && Number.isInteger(entry)) n = entry;
    else if (typeof entry === 'string') {
      const m = entry.match(/^\s*S?(\d+)\s*$/i);
      if (m) n = Number(m[1]);
    }
    if (n !== null && n >= 1 && n <= max && !ids.includes(n)) ids.push(n);
  }
  return ids.length ? ids : null;
}

/**
 * Pick the cut from a ranking. Llama ranks well but can't add up word
 * counts, so the server does the arithmetic: walk the ranking (then any
 * sentences it left out, in reading order) and keep each sentence that fits
 * under the 15% ceiling, stopping as soon as the 15% band floor is reached.
 * Returns ids in reading order. Oversized sentences are skipped; only when
 * none fit is the top sentence kept alone, even if it exceeds the ceiling.
 */
export function fillToTarget(
  ranking: number[],
  sentences: CutdownSentence[],
  target: CutdownLength,
): number[] {
  const words = new Map(sentences.map((s) => [s.id, s.words]));
  const ranked = ranking.filter((id) => words.has(id));
  const order = [...ranked, ...sentences.map((s) => s.id).filter((id) => !ranked.includes(id))];
  const goal = targetWords(target);
  const ceiling = Math.floor(goal * (1 + ON_TARGET_TOLERANCE));
  const keep: number[] = [];
  let total = 0;
  for (const id of order) {
    if (total >= goal * (1 - ON_TARGET_TOLERANCE)) break;
    const w = words.get(id) ?? 0;
    if (total + w > ceiling) continue;
    keep.push(id);
    total += w;
  }
  if (keep.length === 0 && order.length) keep.push(order[0]);
  return keep.sort((a, b) => a - b);
}

// ---------- API shapes ----------

/** GET /api/admin/scripts/:id/cutdown */
export interface CutdownInfo {
  id: string;
  title: string;
  project_id: string;
  project_name: string;
  client_name: string;
  doc_version: number;
  words: number;
  /** Unrounded, one decimal. */
  raw_seconds: number;
  /** The editor badge's estimate; null under 20 spoken words. */
  estimate: { words: number; seconds: number } | null;
}

/** POST /api/admin/scripts/:id/cutdown/suggest */
export interface CutdownSuggestion {
  seconds: CutdownLength;
  /** Source version the cut was built from; create must match it. */
  doc_version: number;
  keep: number[];
  words: number;
  total_words: number;
  /** Unrounded, one decimal. */
  runtime_seconds: number;
  verdict: CutdownVerdict;
  kept_sentences: number;
  total_sentences: number;
  rows: CutdownPreviewRow[];
}

/** POST /api/admin/scripts/:id/cutdown/create */
export interface CutdownCreateResult {
  project_id: string;
  project_name: string;
  drafts: Array<{ id: string; slug: string; title: string; seconds: CutdownLength }>;
}
